import React from "react";
import Text from "@/components/marketing/Text";
import Section from "@/components/marketing/Section";
import dayjs from "dayjs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface Author {
  name: string;
  position: string;
  profilePictureUrl: string;
}

interface BlogPostHeaderProps {
  title: string;
  description: string;
  featuredImage: string;
  date: string;
  authors: Author[];
  readingTime: number;
}

const BlogPostHeaderHighlightedInfo = ({ title, data }) => (
  <div className="flex flex-col justify-center p-6 md:p-7 lg:p-12 gap-2 border-b border-r-0 last:border-b-0 md:border-r md:border-b-0 last:border-r-0 border-silvermist-100 h-full">
    <Text weight="semibold" color="primary">{title}</Text>
    <Text color="secondary">{data}</Text>
  </div>
)

const AuthorsList = ({ authors }: { authors: Author[] }) => (
  <div className={`grid grid-cols-1 ${authors.length > 1 ? 'md:grid-cols-' + authors.length : ''}`}>
    {authors.map((author, index) => (
      <div key={index} className="flex items-center gap-4 p-6 md:p-7 lg:p-12 border-b border-r-0 last:border-b-0 md:border-r md:border-b-0 last:border-r-0 border-silvermist-100">
        <Avatar className="w-12 h-12">
          <AvatarImage src={author.profilePictureUrl} alt={author.name} />
          <AvatarFallback>{author.name[0]}</AvatarFallback>
        </Avatar>
        <div>
          <Text weight="semibold">{author.name}</Text>
          <Text color="secondary" size="sm">{author.position}</Text>
        </div>
      </div>
    ))}
  </div>
)

const BlogPostHeader: React.FC<BlogPostHeaderProps> = ({
  title = '',
  description = '',
  featuredImage = '',
  date = '',
  authors = [],
  readingTime = 0,
}) => {
  return (
    <div className="w-full border-t border-silvermist-100">
      <div className="max-w-[1100px] mx-auto border-x border-silvermist-100">
        <Section contentWidth="content-xs" padding="p-none" className="border-x border-dashed border-silvermist-100">
          <div className="flex flex-col gap-4 px-0 py-12 lg:px-12">
            <Text as="h1" color="primary" weight="semibold" size="5xl" tracking="tight" className="max-w-[720px] leading-tighter">{title}</Text>
            <Text color="secondary" size="2xl" className="max-w-[720px] leading-tighter">{description}</Text>
          </div>
        </Section>
      </div>
      <div className="border-y border-silvermist-100">
        <Section padding="p-none" contentWidth="content-sm" className="border-x border-silvermist-100 !px-0">
          <div className="p-2 bg-silvermist-50">
            <img
              src={featuredImage}
              alt={title}
              className="w-full h-auto object-cover rounded-lg"
            />
          </div>
        </Section>
      </div>
      <Section contentWidth="content-sm" padding="p-none" className="border-x border-b border-silvermist-100">
        <AuthorsList authors={authors} />
      </Section>
      <Section contentWidth="content-sm" padding="p-none" className="grid grid-cols-1 !px-0 md:grid-cols-2 border-x border-b border-silvermist-100">
        <BlogPostHeaderHighlightedInfo title="Date" data={dayjs(date).format('MMMM D, YYYY')} />
        <BlogPostHeaderHighlightedInfo title="Reading Time" data={`${readingTime} min read`} />
      </Section>
    </div>
  )
}

export default BlogPostHeader;